import { getPersonalizedMessage } from './claudeCoaching'
import { supabase } from './supabaseClient'

export async function runCoachingEngine(userId, profile, latestScore, latestDaily, latestWeekly) {
  if (!userId || !latestDaily) return

  const today = new Date().toISOString().split('T')[0]

  // Use a simple lock to prevent double execution
  if (window._coachingRunning) return
  window._coachingRunning = true

  try {
    // Delete today's existing coaching messages before regenerating
    await supabase
      .from('coaching_log')
      .delete()
      .eq('user_id', userId)
      .eq('log_date', today)

  const { data: messages } = await supabase
    .from('message_library')
    .select('*')
    .eq('approved_by_physician', true)

  if (!messages || messages.length === 0) return

  const triggered = []

  function addMessage(category, condition, priority) {
    const match = messages.find(m => 
      m.category === category && 
      m.trigger_condition === condition
    )
    if (match) {
      triggered.push({ ...match, priority })
    }
  }

  // ── ANTICOAGULATION ──────────────────────────────
  if (latestDaily.anticoag_applicable) {
    if (latestDaily.anticoag_taken === true) {
      addMessage('Anticoagulation', 'on anticoag, perfect adherence', 'low')
    } else if (latestDaily.anticoag_taken === false) {
      addMessage('Anticoagulation', 'on anticoag, missed >= 1 dose', 'high')
    }
  }

  // ── EXERCISE ─────────────────────────────────────
  const mins = latestDaily.exercise_minutes || 0
  if (mins <= 10) {
    addMessage('Exercise', '0\u201310 min/day average', 'medium')
  } else if (mins < 30) {
    addMessage('Exercise', '11\u201329 min/day average', 'medium')
  } else {
    addMessage('Exercise', '30+ min/day, mixed intensity', 'low')
  }

  // ── SLEEP ────────────────────────────────────────
  const sleep = latestDaily.sleep_hours || 0
  if (profile?.has_sleep_apnea && profile?.uses_cpap && latestWeekly?.cpap_days_used !== null) {
    const cpap = latestWeekly.cpap_days_used
    if (cpap === 7) {
      addMessage('Sleep', 'CPAP compliance 7 days/week', 'low')
    } else if (cpap < 4) {
      addMessage('Sleep', 'CPAP compliance < 4 days/week', 'high')
    }
  } else if (!profile?.has_sleep_apnea) {
    if (sleep < 6) {
      addMessage('Sleep', 'avg < 6h, no sleep apnea', 'high')
    } else if (sleep > 8.5) {
      addMessage('Sleep', 'avg > 8.5h, no sleep apnea', 'medium')
    }
  }

// ── ALCOHOL ──────────────────────────────────────
  if (latestDaily.alcohol_level === 'none') {
    addMessage('Alcohol', 'no alcohol today', 'low')
  } else if (latestDaily.alcohol_level === 'binge') {
    addMessage('Alcohol', 'binge episode logged', 'high')
  } else if (latestDaily.alcohol_level === 'two_three') {
    addMessage('Alcohol', '2-3 drinks today', 'medium')
  } else if (latestDaily.alcohol_level === 'one') {
    addMessage('Alcohol', '1 drink today', 'low')
  }

  const { data: weeklyAlcohol } = await supabase
    .from('daily_checkins')
    .select('alcohol_level')
    .eq('user_id', userId)
    .gte('checkin_date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
    .order('checkin_date', { ascending: false })

  if (weeklyAlcohol && weeklyAlcohol.length > 0) {
    const allClean = weeklyAlcohol.every(c => c.alcohol_level === 'none')
    if (allClean) {
      addMessage('Alcohol', 'no alcohol this week', 'low')
    }
  }

  // ── TOBACCO ──────────────────────────────────────
  if (!latestDaily.nicotine_used) {
    addMessage('Tobacco', 'non-smoker', 'low')
  } else {
    addMessage('Tobacco', 'currently smoking', 'high')
  }

  // ── CAFFEINE ─────────────────────────────────────
  if (latestDaily.caffeine_level === 'four_plus') {
    addMessage('Caffeine', '> 300mg/day', 'medium')
  }

  // ── BLOOD PRESSURE ───────────────────────────────
  if (latestWeekly?.bp_available && latestWeekly?.bp_systolic) {
    const sys = parseInt(latestWeekly.bp_systolic)
    if (sys >= 140) {
      addMessage('Blood pressure', 'systolic > 140', 'high')
    }
  } else if (!profile?.has_bp_cuff) {
    addMessage('Blood pressure', 'no BP cuff', 'low')
  }

  // ── WEIGHT ───────────────────────────────────────
  if (latestWeekly?.weight_available && latestWeekly?.weight_lbs && profile?.weight_lbs_baseline) {
    const baseline = parseFloat(profile.weight_lbs_baseline)
    const current = parseFloat(latestWeekly.weight_lbs)
    const pctLoss = ((baseline - current) / baseline) * 100
    if (pctLoss >= 3) {
      addMessage('Weight', 'lost 3\u20139% body weight', 'low')
    } else if (profile?.bmi_baseline > 30) {
      addMessage('Weight', 'BMI > 30, no recent loss', 'medium')
    }
  }

  // ── LIPIDS ───────────────────────────────────────
  if (latestWeekly?.lipids_available) {
    const hdl = parseFloat(latestWeekly.hdl)
    const trig = parseFloat(latestWeekly.triglycerides)
    if (hdl < 40) {
      addMessage('Lipids', 'HDL < 40', 'medium')
    }
    if (trig > 200) {
      addMessage('Lipids', 'triglycerides > 200', 'medium')
    }
  }

  // ── GENERAL ──────────────────────────────────────
  const { count } = await supabase
    .from('daily_checkins')
    .select('id', { count: 'exact' })
    .eq('user_id', userId)

  if (count === 1) {
    addMessage('General', 'enrollment \u2013 week 1', 'low')
  }

  // ── PRIORITY SORT ────────────────────────────────
  const priorityOrder = { high: 0, medium: 1, low: 2 }
  triggered.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])

  // ── LIMIT TO TOP 4 MESSAGES ──────────────────────
  const topMessages = triggered.slice(0, 4)


if (topMessages.length === 0) {
    console.log('No messages triggered')
    return
  }

  // ── SAVE TO COACHING LOG ─────────────────────────
const patientData = {
    age: profile?.age,
    sex: profile?.sex,
    exercise_minutes: latestDaily?.exercise_minutes,
    exercise_intensity: latestDaily?.exercise_intensity,
    sleep_hours: latestDaily?.sleep_hours,
    alcohol_level: latestDaily?.alcohol_level,
    caffeine_level: latestDaily?.caffeine_level,
    anticoag_taken: latestDaily?.anticoag_taken,
    current_weight: latestWeekly?.weight_lbs,
    baseline_weight: profile?.weight_lbs_baseline,
    bp_systolic: latestWeekly?.bp_systolic,
    bp_diastolic: latestWeekly?.bp_diastolic,
    htn: profile?.htn,
    diabetes: profile?.diabetes,
    goal_timeframe: profile?.goal_timeframe,
  }

  const logEntries = await Promise.all(topMessages.map(async msg => {
    const baseMessage = personalizeMessage(msg.template_text, profile, latestWeekly, latestDaily)
    const personalizedMessage = await getPersonalizedMessage(baseMessage, patientData)
    return {
      user_id: userId,
      log_date: today,
      category: msg.category,
      message_template_id: msg.id,
      personalized_message: personalizedMessage,
      priority: msg.priority,
      ai_personalized: true,
      physician_approved: true,
      was_read: false
    }
  }))

  await supabase.from('coaching_log').insert(logEntries)
  } finally {
    window._coachingRunning = false
  }

}

function personalizeMessage(template, profile, weekly, daily) {
  let msg = template

  if (profile?.weight_lbs_baseline && weekly?.weight_lbs) {
    const change = (parseFloat(weekly.weight_lbs) - parseFloat(profile.weight_lbs_baseline)).toFixed(1)
    const pct = (((parseFloat(profile.weight_lbs_baseline) - parseFloat(weekly.weight_lbs)) / parseFloat(profile.weight_lbs_baseline)) * 100).toFixed(1)
    msg = msg.replace('[WEIGHT_CHANGE]', `${Math.abs(change)} lbs`)
    msg = msg.replace('[WEIGHT_PCT]', `${pct}%`)
  }

  if (weekly?.bp_systolic) {
    msg = msg.replace('[BP_SYSTOLIC]', weekly.bp_systolic)
  }

  if (daily?.exercise_minutes) {
    msg = msg.replace('[EXERCISE_MINS]', daily.exercise_minutes)
  }

  if (weekly?.cpap_days_used !== null && weekly?.cpap_days_used !== undefined) {
    msg = msg.replace('[CPAP_DAYS]', weekly.cpap_days_used)
  }

  return msg
}